import JSZip from 'jszip'
import api from 'helpers/api'
import { IDownloadZipProgress, ISpreadsheetData } from 'components/spreadsheet/types'
import { IFile, ITableViewColour, ITableViewFilter } from 'types'
import {
  buildSlicerRuntimeFilters,
  convertToApiColour,
  convertToApiFilter,
  getRowValue,
  mergeSlicerFilters
} from 'components/spreadsheet/helpers/functions'

export type ExportFormat = 'csv' | 'xlsx'

// Body of POST /table/views/{id}/export. The view's saved filters always apply on the
// server; these carry what the user sees now, saved or not, so the file matches the screen.
export interface IExportTableViewBody {
  format: ExportFormat
  filter?: ITableViewFilter[]
  sort?: string[]
  hidden?: string[]
  colour?: ITableViewColour[]
  processId?: string
  displayCommentRows?: number
  timezone?: string
}

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/

export const exportFilename = (spreadsheetData: ISpreadsheetData, format: ExportFormat): string =>
  `${spreadsheetData.tableDetails.name} - ${spreadsheetData.viewDetails.name}.${format}`

export function buildExportRequestBody(
  spreadsheetData: ISpreadsheetData,
  slicerSelections: Record<string, string[]> | undefined,
  format: ExportFormat
): IExportTableViewBody {
  const { viewDetails, userConfiguration, processId } = spreadsheetData
  const slicerFilters = buildSlicerRuntimeFilters(viewDetails.columns, slicerSelections)
  const filters = mergeSlicerFilters(userConfiguration.filterSettings, slicerFilters)

  const body: IExportTableViewBody = { format }
  if (filters.length > 0) body.filter = convertToApiFilter(filters)
  // Always sent, even empty: no sort is a sort too, and leaving it out means "the saved sort".
  body.sort = userConfiguration.sortSettings.map((sort) => `${sort.columnName}:${sort.direction}`)
  if (userConfiguration.hiddenColumns.length > 0) body.hidden = userConfiguration.hiddenColumns
  if (processId) body.processId = processId
  if (viewDetails.displayCommentRows !== undefined) body.displayCommentRows = viewDetails.displayCommentRows
  if (format === 'xlsx') {
    // A rule still being typed in the colour picker may not be a colour yet; leave it out.
    body.colour = convertToApiColour(
      userConfiguration.colourSettings.filter(
        (rule) => HEX_COLOUR.test(rule.backgroundColour) && HEX_COLOUR.test(rule.fontColour)
      )
    )
    body.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  }
  return body
}

/**
 * The files a ZIP export would take: those in the view's attachment columns (or just the one
 * named), in the rows the active filters keep visible. `rows` holds the full (unfiltered) set;
 * visibility is expressed via the parallel `visibleBitmap` (null = no filter).
 */
export const exportableAttachments = (
  spreadsheetData: ISpreadsheetData,
  columnPublicId?: string,
  columnIdToIndex?: Map<string, number>
): IFile[] => {
  const { visibleRows, filesIn } = attachmentLookup(spreadsheetData, columnPublicId, columnIdToIndex)
  return visibleRows.flatMap(filesIn)
}

/** Whether a ZIP export would take anything: stops at the first file, rather than gathering them all. */
export const hasExportableAttachments = (spreadsheetData: ISpreadsheetData): boolean => {
  const { visibleRows, filesIn } = attachmentLookup(spreadsheetData)
  return visibleRows.some((row) => filesIn(row).length > 0)
}

const attachmentLookup = (
  spreadsheetData: ISpreadsheetData,
  columnPublicId?: string,
  columnIdToIndex?: Map<string, number>
) => {
  const attachmentColumns = spreadsheetData.viewDetails.columns.filter(
    (c) => c.kind === 'attachment' && (!columnPublicId || c.publicId === columnPublicId)
  )
  const colIdToIdx = columnIdToIndex ?? spreadsheetData.columnIdToIndex
  const bitmap = spreadsheetData.visibleBitmap
  const visibleRows = bitmap ? spreadsheetData.rows.filter((_, i) => bitmap[i]) : spreadsheetData.rows
  const filesIn = (row: ISpreadsheetData['rows'][number]): IFile[] =>
    attachmentColumns.flatMap(
      (col) => (getRowValue(row, col.publicId, colIdToIdx, spreadsheetData.columnStore) as IFile[] | null) ?? []
    )
  return { visibleRows, filesIn }
}

export async function exportAttachmentsAsZip(
  spreadsheetData: ISpreadsheetData,
  setZipProgress: React.Dispatch<React.SetStateAction<IDownloadZipProgress>>,
  displayErrorMessage: (error: unknown) => void,
  columnPublicId?: string,
  columnIdToIndex?: Map<string, number>
): Promise<boolean> {
  // True once the ZIP is on its way; false if building it failed (the error is shown).
  try {
    const zip = new JSZip()
    const nameCounts = new Map<string, number>()

    /* --- ① gather every attachment first so we know the total ------------ */
    const tasks = exportableAttachments(spreadsheetData, columnPublicId, columnIdToIndex).map((file) => ({ file }))
    setZipProgress({ done: 0, total: tasks.length })

    /* --- ② download + add to ZIP ---------------------------------------- */
    let failed = 0
    for (const { file } of tasks) {
      try {
        const { data } = await api.signUrl(file.url)
        const blob = await fetch(data.url).then((r) => r.blob())

        const original = file.filename.replace(/[\\/:]/g, '_')
        const dot = original.lastIndexOf('.')
        const base = dot !== -1 ? original.slice(0, dot) : original
        const ext = dot !== -1 ? original.slice(dot) : ''
        const count = (nameCounts.get(original) ?? 0) + 1
        nameCounts.set(original, count)
        const arcName = count === 1 ? original : `${base} (${count})${ext}`

        zip.file(arcName, blob)
      } catch {
        // Left out of the ZIP, and counted, so the user hears which ones are missing.
        failed++
      }
      /* bump the counter after each attempt */
      setZipProgress((prev) => ({ ...prev, done: prev.done + 1 }))
    }

    // Nothing came through: no ZIP, and the caller keeps its menu open to try again.
    if (tasks.length > 0 && failed === tasks.length) {
      displayErrorMessage(
        tasks.length === 1
          ? "The attachment couldn't be downloaded, so no ZIP was made. Try again."
          : `None of the ${tasks.length} attachments could be downloaded, so no ZIP was made. Try again.`
      )
      return false
    }

    /* --- ③ build, download, cleanup ------------------------------------- */
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const url = URL.createObjectURL(zipBlob)
    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: `${spreadsheetData.tableDetails.name} - ${spreadsheetData.viewDetails.name} attachments.zip`
    })
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 0)
    if (failed > 0) {
      displayErrorMessage(
        `${failed} of ${tasks.length} attachments couldn't be downloaded and were left out of the ZIP.`
      )
    }
    return true
  } catch (err) {
    displayErrorMessage(err)
    return false
  } finally {
    setZipProgress({ done: 0, total: 0 }) // reset
  }
}
